import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import { api, ErroHttp } from '@/lib/api';

/**
 * Inventário: o agrupamento de escopos do ERP que o PBS não tem.
 * Os tipos espelham `apps/api/src/modules/inventario/queries.ts`.
 */

export type EtapaEscopo =
  | 'montagem'
  | 'contagem'
  | 'recontagem'
  | 'final'
  | 'integracao'
  | 'integrado';

export interface EscopoInventario {
  escopo: number;
  criadoEm: string;
  criadoPor: string | null;
  empresa: number;
  centro: number;
  ruas: string[];
  localizadores: number;
  produtos: number;
  coletados: [number, number, number];
  /** Endereços com saldo na foto (alvo da 1ª); `null` antes de congelar. */
  comSaldo: number | null;
  bipadosComSaldo: number;
  /** Endereços vazios na foto em que algo foi bipado. */
  sobras: number;
  perdidas: [number, number, number];
  ultimaColeta: string | null;
  congeladoEm: string | null;
  /** Data-base da foto do saldo, AAAA-MM-DD. */
  dataBase: string | null;
  contagemFechadaEm: string | null;
  recontagemFechadaEm: string | null;
  finalFechadoEm: string | null;
  integradoEm: string | null;
  divergentes: number;
  etapa: EtapaEscopo;
  origem?: 'WMS' | 'PBS' | undefined;
}

/** GERAL = montador de sempre; CICLICO = escopos gerados por rua ou pelos SKUs. */
export type TipoInventario = 'GERAL' | 'CICLICO';
export type CriterioCiclico = 'RUA' | 'SKU';

export interface InventarioResumo {
  id: number;
  nome: string;
  empresa: number;
  centro: number;
  situacao: 'ABERTO' | 'ENCERRADO';
  criadoEm: string;
  criadoPor: string | null;
  tipo: TipoInventario;
  criterio: CriterioCiclico | null;
  escopos: number;
  numerosEscopos: number[];
  localizadores: number;
  coletados: number;
  integrados: number;
  alvo: number;
  bipadosAlvo: number;
  sobras: number;
  perdidas: number;
  divergentes: number;
  etapas: Partial<Record<EtapaEscopo, number>>;
  /** 1ª contagem de cada escopo congelado — mostra o atrasado que a média esconde. */
  porEscopo: { escopo: number; alvo: number; bipados: number; etapa: EtapaEscopo }[];
  ultimaColeta: string | null;
}

export interface Inventario {
  id: number;
  nome: string;
  empresa: number;
  centro: number;
  situacao: 'ABERTO' | 'ENCERRADO';
  criadoEm: string;
  criadoPor: string | null;
  tipo: TipoInventario;
  criterio: CriterioCiclico | null;
}

export interface ConflitoEndereco {
  localizador: number;
  endereco: string | null;
  escopo: number;
  outroEscopo: number;
  outroInventario: number | null;
  outroCriadoEm: string;
}

export interface DetalheInventario {
  inventario: Inventario;
  escopos: EscopoInventario[];
  conflitos: ConflitoEndereco[];
}

function tentarDeNovo(tentativas: number, erro: unknown): boolean {
  if (erro instanceof ErroHttp && (erro.naoAutenticado || erro.semPermissao || erro.status < 500)) {
    return false;
  }
  return tentativas < 2;
}

export function useInventarios(
  centro: number | null,
): UseQueryResult<{ inventarios: InventarioResumo[] }, unknown> {
  return useQuery({
    queryKey: ['inventarios', centro],
    queryFn: () => api.get<{ inventarios: InventarioResumo[] }>('/api/inventarios', { centro }),
    enabled: centro !== null,
    retry: tentarDeNovo,
    placeholderData: (anterior) => anterior,
  });
}

/**
 * O andamento muda a cada bipe do coletor: 30 s de folga e atualização a cada
 * minuto enquanto a página está aberta — é o painel que fica na TV do CD.
 */
export function useInventario(id: number | null): UseQueryResult<DetalheInventario, unknown> {
  return useQuery({
    queryKey: ['inventario', id],
    queryFn: () => api.get<DetalheInventario>(`/api/inventarios/${id}`),
    enabled: id !== null,
    retry: tentarDeNovo,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useCriarInventario() {
  const cliente = useQueryClient();
  return useMutation<
    { id: number },
    unknown,
    { nome: string; empresa: number; centro: number; tipo?: TipoInventario; criterio?: CriterioCiclico | null }
  >({
    mutationFn: (dados) => api.post<{ id: number }>('/api/inventarios', dados),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['inventarios'] }),
  });
}

export function useAtualizarInventario(id: number) {
  const cliente = useQueryClient();
  return useMutation<unknown, unknown, { nome?: string; situacao?: 'ABERTO' | 'ENCERRADO' }>({
    mutationFn: (dados) => api.patch(`/api/inventarios/${id}`, dados),
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['inventario', id] });
      void cliente.invalidateQueries({ queryKey: ['inventarios'] });
    },
  });
}

export interface EnderecoOcupado {
  localizador: number;
  escopo: number;
  inventario: number | null;
}

/** Endereços do centro do escopo em escopo aberto: o montador avisa. */
export function useEnderecosOcupados(
  id: number | null,
  centro: number | null,
): UseQueryResult<{ enderecos: EnderecoOcupado[] }, unknown> {
  return useQuery({
    queryKey: ['inventario', id, 'ocupados', centro],
    queryFn: () =>
      api.get<{ enderecos: EnderecoOcupado[] }>(`/api/inventarios/${id}/enderecos-ocupados`, { centro }),
    enabled: id !== null && centro !== null,
    retry: tentarDeNovo,
    staleTime: 15_000,
  });
}

export interface EscopoCriado {
  escopo: number;
  localizadores: number;
  produtos: number;
}

export function useCriarEscopo(id: number) {
  const cliente = useQueryClient();
  return useMutation<EscopoCriado, unknown, { localizadores: number[]; descricao: string; centro: number }>({
    mutationFn: (dados) => api.post<EscopoCriado>(`/api/inventarios/${id}/escopos`, dados),
    // Também no erro: a recusa por sobreposição significa que os ocupados mudaram.
    // Espera o inventário recarregar: o "carregando" só some com a tela já
    // mostrando a etapa nova (sem isso, parecia preciso dar refresh).
    onSettled: async () => {
      void cliente.invalidateQueries({ queryKey: ['inventarios'] });
      void cliente.invalidateQueries({ queryKey: ['inventario', id], exact: false, refetchType: 'none' });
      await cliente.refetchQueries({ queryKey: ['inventario', id], exact: true });
      void cliente.invalidateQueries({ queryKey: ['inventario', id] });
    },
  });
}

export interface ProdutosReinseridos {
  escopo: number;
  antes: number;
  depois: number;
  entraram: number;
  sairam: number;
  /** Ficaram por estarem na foto do congelamento (a função 30 tiraria). */
  mantidos: number;
  amostraEntraram: number[];
  erro?: string;
}

export interface ResultadoReinsercao {
  escopos: ProdutosReinseridos[];
  pulados: { escopo: number; etapa: EtapaEscopo }[];
}

/** Função 30 do PBS outra vez, em todos os escopos em montagem ou 1ª contagem. */
export function useReinserirProdutos(id: number) {
  const cliente = useQueryClient();
  return useMutation<ResultadoReinsercao, unknown, void>({
    mutationFn: () => api.post<ResultadoReinsercao>(`/api/inventarios/${id}/reinserir-produtos`, {}),
    onSettled: () => {
      void cliente.invalidateQueries({ queryKey: ['inventario', id] });
      void cliente.invalidateQueries({ queryKey: ['inventarios'] });
    },
  });
}

export interface Congelamento {
  escopo: number;
  processamento: number;
  dataBase: string;
  linhas: number;
  produtos: number;
}

export function useCongelarEscopo(id: number) {
  const cliente = useQueryClient();
  return useMutation<Congelamento, unknown, { escopo: number; dataBase: string }>({
    mutationFn: ({ escopo, dataBase }) =>
      api.post<Congelamento>(`/api/inventarios/${id}/escopos/${escopo}/congelar`, { dataBase }),
    // Espera o inventário recarregar: o "carregando" só some com a tela já
    // mostrando a etapa nova (sem isso, parecia preciso dar refresh).
    onSettled: async () => {
      void cliente.invalidateQueries({ queryKey: ['inventarios'] });
      void cliente.invalidateQueries({ queryKey: ['inventario', id], exact: false, refetchType: 'none' });
      await cliente.refetchQueries({ queryKey: ['inventario', id], exact: true });
      void cliente.invalidateQueries({ queryKey: ['inventario', id] });
    },
  });
}

export interface ItemEscopo {
  localizador: number;
  endereco: string | null;
  rua: string | null;
  produto: number | null;
  descricao: string | null;
  ean: string | null;
  saldoAtual: number | null;
  saldoCongelado: number | null;
}

export function useItensEscopo(
  id: number,
  escopo: number | null,
): UseQueryResult<{ itens: ItemEscopo[] }, unknown> {
  return useQuery({
    queryKey: ['inventario', id, 'escopo', escopo, 'itens'],
    queryFn: () => api.get<{ itens: ItemEscopo[] }>(`/api/inventarios/${id}/escopos/${escopo}/itens`),
    enabled: escopo !== null,
    retry: tentarDeNovo,
    staleTime: 60_000,
  });
}

export type EtapaFechamento = 'contagem' | 'recontagem' | 'final' | 'integracao';
/** O que se desfaz, de trás para frente: inclui descongelar e excluir o escopo. */
export type EtapaDesfazer = 'escopo' | 'congelamento' | EtapaFechamento;

export interface Fechamento {
  etapa: EtapaFechamento;
  escopo: number;
  fechamento: number;
  linhas: number;
  divergentes: number;
  lancamentos?: { linhas: number; entradas: number; saidas: number } | undefined;
}

export function useFecharEtapa(id: number) {
  const cliente = useQueryClient();
  return useMutation<
    Fechamento,
    unknown,
    {
      escopo: number;
      etapa: EtapaFechamento;
      copiarRecontagem?: boolean;
      movimento?: string;
      contabilizar?: boolean;
    }
  >({
    mutationFn: ({ escopo, ...corpo }) =>
      api.post<Fechamento>(`/api/inventarios/${id}/escopos/${escopo}/fechar`, corpo),
    // Espera o inventário recarregar: o "carregando" só some com a tela já
    // mostrando a etapa nova (sem isso, parecia preciso dar refresh).
    onSettled: async () => {
      void cliente.invalidateQueries({ queryKey: ['inventarios'] });
      void cliente.invalidateQueries({ queryKey: ['inventario', id], exact: false, refetchType: 'none' });
      await cliente.refetchQueries({ queryKey: ['inventario', id], exact: true });
      void cliente.invalidateQueries({ queryKey: ['inventario', id] });
    },
  });
}

export interface ParametroConsulta {
  parametro: string;
  titulo: string;
  tamanho: number | null;
  tipo: 'escopo' | 'escopo_ini' | 'escopo_fim' | 'texto';
}

export interface CampoConsulta {
  campo: string;
  titulo: string;
  totalizar: boolean;
  decimais: number | null;
}

export interface DefinicaoConsulta {
  consulta: string;
  descricao: string;
  ultimaAlteracao: string | null;
  parametros: ParametroConsulta[];
  campos: CampoConsulta[];
}

export interface ResultadoConsulta {
  colunas: string[];
  linhas: Record<string, unknown>[];
  truncado: boolean;
  ms: number;
  /** Escopos que existem na faixa inicial/final pedida (só em consulta por faixa). */
  escoposNaFaixa?: number[];
}

/** As definições mudam só quando alguém mexe no PBS: 10 min de folga. */
export function useConsultasInventario(): UseQueryResult<{ consultas: DefinicaoConsulta[] }, unknown> {
  return useQuery({
    queryKey: ['inventario', 'consultas'],
    queryFn: () => api.get<{ consultas: DefinicaoConsulta[] }>('/api/inventarios/consultas'),
    retry: tentarDeNovo,
    staleTime: 10 * 60_000,
  });
}

export function useExecutarConsulta() {
  return useMutation<ResultadoConsulta, unknown, { consulta: string; valores: Record<string, string> }>({
    mutationFn: ({ consulta, valores }) =>
      api.post<ResultadoConsulta>(`/api/inventarios/consultas/${encodeURIComponent(consulta)}`, { valores }),
  });
}

export interface EnderecoInventario {
  localizador: number;
  escopo: number;
  bipado: [boolean, boolean, boolean];
  ultimoBipe: string | null;
  divergente: [boolean, boolean, boolean];
  comSaldo: boolean | null;
  /** Validado no coletor nesta contagem (mesmo sem produto). */
  visitado: [boolean, boolean, boolean];
  /** Cada produto do endereço: o que a foto esperava e o que cada contagem achou. */
  itens: ItemDoMapa[];
}

export interface ItemDoMapa {
  produto: number;
  /** Saldo da foto do congelamento; `null` se o escopo ainda não congelou. */
  esperado: number | null;
  /** Soma das coletas em cada contagem (1ª, recontagem, final). */
  contado: [number, number, number];
  /** Divergência do fechamento final do PBS; `null` se o escopo não fechou a final. */
  divergenciaOficial: number | null;
}

/** `custo`: último custo de compra (senão preço de fábrica) — a regra da prévia de fechamento. */
export type ProdutosDoMapa = Record<number, { descricao: string | null; ean: string | null; custo: number }>;

export function useEnderecosInventario(
  id: number | null,
): UseQueryResult<{ enderecos: EnderecoInventario[]; produtos: ProdutosDoMapa }, unknown> {
  return useQuery({
    queryKey: ['inventario', id, 'enderecos'],
    queryFn: () =>
      api.get<{ enderecos: EnderecoInventario[]; produtos: ProdutosDoMapa }>(`/api/inventarios/${id}/enderecos`),
    enabled: id !== null,
    retry: tentarDeNovo,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export interface ContadorInventario {
  cracha: string;
  nome: string | null;
  escopo: number;
  contagem: number;
  enderecos: number;
  itens: number;
  unidades: number;
  primeiroEnvio: string | null;
  ultimoEnvio: string | null;
}

export function useEquipeInventario(id: number): UseQueryResult<{ equipe: ContadorInventario[] }, unknown> {
  return useQuery({
    queryKey: ['inventario', id, 'equipe'],
    queryFn: () => api.get<{ equipe: ContadorInventario[] }>(`/api/inventarios/${id}/equipe`),
    retry: tentarDeNovo,
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export interface PreviaIntegracao {
  empresaWms: boolean;
  linhas: number;
  entradas: number;
  saidas: number;
  produtos: number;
}

export function usePreviaIntegracao(id: number, escopo: number): UseQueryResult<PreviaIntegracao, unknown> {
  return useQuery({
    queryKey: ['inventario', id, 'escopo', escopo, 'previa-integracao'],
    queryFn: () => api.get<PreviaIntegracao>(`/api/inventarios/${id}/escopos/${escopo}/previa-integracao`),
    retry: tentarDeNovo,
  });
}

export function useDesfazerEtapa(id: number) {
  const cliente = useQueryClient();
  return useMutation<
    { etapa: EtapaDesfazer; escopo: number; linhasFormulario: number },
    unknown,
    { escopo: number; etapa: EtapaDesfazer }
  >({
    mutationFn: ({ escopo, etapa }) => api.post(`/api/inventarios/${id}/escopos/${escopo}/desfazer`, { etapa }),
    onSuccess: () => void cliente.invalidateQueries({ queryKey: ['inventario', id, 'historico'] }),
    // Espera o inventário recarregar: o "carregando" só some com a tela já
    // mostrando a etapa nova (sem isso, parecia preciso dar refresh).
    onSettled: async () => {
      void cliente.invalidateQueries({ queryKey: ['inventarios'] });
      void cliente.invalidateQueries({ queryKey: ['inventario', id], exact: false, refetchType: 'none' });
      await cliente.refetchQueries({ queryKey: ['inventario', id], exact: true });
      void cliente.invalidateQueries({ queryKey: ['inventario', id] });
    },
  });
}

export interface ItemEmRisco {
  localizador: number;
  endereco: string | null;
  produto: number;
  descricao: string | null;
  unidades: number;
  custo: number;
  valor: number;
  nuncaBipado: boolean;
}

export interface PreviaFechamento {
  contagem: 1 | 2 | 3;
  coletas: number;
  enderecosSemBipe: number;
  unidadesEmRisco: number;
  produtosEmRisco: number;
  valorEmRisco: number;
  itens: ItemEmRisco[];
  /** Divergência até agora nos endereços já bipados nesta contagem. */
  divergencias: ItemDivergente[];
  /** Códigos que o coletor recusou como "Produto Invalido" nesta contagem. */
  codigosSemCadastro: CodigoSemCadastro[];
}

export interface ItemDivergente {
  localizador: number;
  endereco: string | null;
  produto: number;
  descricao: string | null;
  foto: number;
  contado: number;
  diferenca: number;
  custo: number;
  valor: number;
}

export interface CodigoSemCadastro {
  codigo: string;
  localizador: number | null;
  endereco: string | null;
  leituras: number;
}

export function usePreviaFechamento(
  id: number,
  escopo: number,
  contagem: 1 | 2 | 3,
): UseQueryResult<PreviaFechamento, unknown> {
  return useQuery({
    queryKey: ['inventario', id, 'escopo', escopo, 'previa-fechamento', contagem],
    queryFn: () =>
      api.get<PreviaFechamento>(`/api/inventarios/${id}/escopos/${escopo}/previa-fechamento`, { contagem }),
    retry: tentarDeNovo,
    staleTime: 15_000,
  });
}

export interface LinhaAntesDepois {
  escopo: number;
  localizador: number;
  endereco: string | null;
  rua: string | null;
  produto: number;
  descricao: string | null;
  /** Saldo da foto do congelamento. */
  antes: number;
  /** Lançado no kardex pela integração (entrada − saída). */
  ajuste: number;
  depois: number;
  custo: number;
  valor: number;
}

export function useAntesDepois(id: number | null): UseQueryResult<{ linhas: LinhaAntesDepois[] }, unknown> {
  return useQuery({
    queryKey: ['inventario', id, 'antes-depois'],
    queryFn: () => api.get<{ linhas: LinhaAntesDepois[] }>(`/api/inventarios/${id}/antes-depois`),
    enabled: id !== null,
    retry: tentarDeNovo,
    staleTime: 60_000,
  });
}

export interface ProdutoComEnderecos {
  produto: number;
  descricao: string | null;
  marca: string | null;
  enderecos: { localizador: number; saldo: number }[];
}

export function useProdutosMontador(
  id: number,
  centro: number | null,
  q: string,
): UseQueryResult<{ produtos: ProdutoComEnderecos[] }, unknown> {
  return useQuery({
    queryKey: ['inventario', id, 'produtos', centro, q],
    queryFn: () => api.get<{ produtos: ProdutoComEnderecos[] }>(`/api/inventarios/${id}/produtos`, { q, centro }),
    enabled: centro !== null && q.trim().length >= 2,
    retry: tentarDeNovo,
    staleTime: 60_000,
  });
}

export function useRitmoContagem(): UseQueryResult<{ enderecosPorHora: number | null }, unknown> {
  return useQuery({
    queryKey: ['inventarios-ritmo'],
    queryFn: () => api.get<{ enderecosPorHora: number | null }>('/api/inventarios-ritmo'),
    staleTime: 3_600_000,
  });
}

export interface AcaoHistorico {
  dataHora: string;
  login: string | null;
  nome: string | null;
  acao: string;
  resultado: string;
  detalhe: string | null;
}

export function useHistoricoInventario(id: number): UseQueryResult<{ historico: AcaoHistorico[] }, unknown> {
  return useQuery({
    queryKey: ['inventario', id, 'historico'],
    queryFn: () => api.get<{ historico: AcaoHistorico[] }>(`/api/inventarios/${id}/historico`),
    retry: tentarDeNovo,
    staleTime: 30_000,
  });
}

// ---------------------------------------------------------------------------
// Inventário cíclico: gerar escopos por rua ou pelos SKUs
// ---------------------------------------------------------------------------

/** Endereços que já estão num escopo aberto — aviso, não trava. */
export interface Sobreposicao {
  escopo: number;
  inventario: number | null;
  enderecos: number;
}

export interface RuaDoCentro {
  rua: string;
  enderecos: number;
  /** Último escopo integrado com endereço na rua; `null` = nunca inventariada. */
  ultimaIntegracao: string | null;
  emEscopoAberto: Sobreposicao[];
}

export function useRuasCiclico(centro: number | null): UseQueryResult<{ ruas: RuaDoCentro[] }, unknown> {
  return useQuery({
    queryKey: ['inventario', 'ciclico', 'ruas', centro],
    queryFn: () => api.get<{ ruas: RuaDoCentro[] }>('/api/inventarios/ciclico/ruas', { centro }),
    enabled: centro !== null,
    retry: tentarDeNovo,
    staleTime: 5 * 60_000,
  });
}

export interface SelecaoCiclica {
  criterio: CriterioCiclico;
  ruas?: string[];
  produtos?: number[];
}

export interface PreviaCiclica {
  grupos: { rotulo: string; enderecos: number; semVaga: number[]; emEscopoAberto: Sobreposicao[] }[];
}

export function usePreviaCiclico(
  centro: number | null,
  selecao: SelecaoCiclica | null,
): UseQueryResult<PreviaCiclica, unknown> {
  return useQuery({
    queryKey: ['inventario', 'ciclico', 'previa', centro, selecao],
    queryFn: () => api.post<PreviaCiclica>('/api/inventarios/ciclico/previa', { centro, ...selecao }),
    enabled: centro !== null && selecao !== null,
    retry: tentarDeNovo,
    staleTime: 30_000,
    placeholderData: (anterior) => anterior,
  });
}

export interface ResultadoCiclico {
  gerados: { escopo: number; localizadores: number; produtos: number; rotulo: string }[];
  pulados: { rotulo: string; motivo: string }[];
}

export function useGerarCiclico() {
  const cliente = useQueryClient();
  return useMutation<ResultadoCiclico, unknown, { inventario: number } & SelecaoCiclica>({
    mutationFn: ({ inventario, ...selecao }) =>
      api.post<ResultadoCiclico>(`/api/inventarios/${inventario}/ciclico/gerar`, selecao),
    onSettled: (_r, _e, v) => {
      void cliente.invalidateQueries({ queryKey: ['inventarios'] });
      void cliente.invalidateQueries({ queryKey: ['inventario', v.inventario] });
    },
  });
}
